import {Alert, Button, Group, Modal, Stack, Text} from '@mantine/core';
import React from 'react';

import {isSimpleSequence} from 'biojs/Util';
import runModal, {RunModalContainerProps, RunModalProps} from 'ui-library/runModal';

import {SelectedComponents, separateComponentsByEntityType} from './ComponentUtils';

export function looksLikeNucleotideOnlySequence(sequence: string): boolean {
  return sequence.length > 0 && isSimpleSequence(sequence);
}

export function hasNucleotideOnlySequences(components: SelectedComponents): boolean {
  const {proteinComponents} = separateComponentsByEntityType(components);
  return Object.values(proteinComponents).some((component) =>
    looksLikeNucleotideOnlySequence(component.aminoAcids)
  );
}

interface DNASequenceWarningModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

interface DNASequenceWarningModalContentProps {
  onCancel: () => void;
  onConfirm: () => void;
}

interface RunDNASequenceWarningModalContentProps extends RunModalProps<boolean> {}

function DNASequenceWarningModalContent({
  onCancel,
  onConfirm,
}: DNASequenceWarningModalContentProps): JSX.Element {
  return (
    <Stack spacing="md">
      <Alert color="yellow" variant="light">
        One or more selected AA sequences appear to be DNA or RNA sequences instead (containing only A, G, C,
        T, or U nucleotides).
      </Alert>
      <Text size="sm">
        The given sequences are categorized as AA sequences. Submitting nucleotide sequences instead may
        result in prediction failures or unexpected results, as the nucleotides will be interpreted as amino
        acids (e.g. Alanine instead of Adenine).
      </Text>
      <Text size="sm" weight={600}>
        Are you sure you want to proceed?
      </Text>
      <Group position="right" spacing="sm">
        <Button variant="tertiary" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onConfirm}>Continue Anyway</Button>
      </Group>
    </Stack>
  );
}

function RunDNASequenceWarningModalContent({close}: RunDNASequenceWarningModalContentProps): JSX.Element {
  return (
    <DNASequenceWarningModalContent
      onCancel={() => {
        close(false);
      }}
      onConfirm={() => {
        close(true);
      }}
    />
  );
}

export async function openDNASequenceWarningModal(containerProps?: RunModalContainerProps): Promise<boolean> {
  return (
    (await runModal(
      RunDNASequenceWarningModalContent,
      {},
      {
        title: 'Possible DNA Sequence Detected',
        size: 'md',
        ...containerProps,
      }
    )) ?? false
  );
}

export default function DNASequenceWarningModal({
  opened,
  onClose,
  onConfirm,
}: DNASequenceWarningModalProps): JSX.Element {
  return (
    <Modal opened={opened} onClose={onClose} title="Possible DNA Sequence Detected" size="md">
      <DNASequenceWarningModalContent onCancel={onClose} onConfirm={onConfirm} />
    </Modal>
  );
}
